/**
 * Configuración de imágenes locales soportadas
 * Define qué imágenes están disponibles localmente y pueden usarse en lugar del CDN
 * cuando NEXT_PUBLIC_USE_LOCAL_IMAGES=true
 */

// Equipos soportados (IDs de equipos que tienen imagen local)
export const SUPPORTED_TEAMS = [
    529, // Barcelona
    530, // Atlético de Madrid
    531, // Athletic Club
    532, // Valencia
    533, // Villarreal
    534, // Las Palmas
    536, // Sevilla
    538, // Celta de Vigo
    540, // Espanyol
    541, // Real Madrid
    542, // Alavés
    543, // Real Betis
    546, // Getafe
    547, // Girona
    548, // Real Sociedad
    715, // (verificar)
    720, // Valladolid
    723, // (verificar)
    724, // (verificar)
    727, // Osasuna
    728, // Rayo Vallecano
    797, // Elche
    798, // Mallorca
    539, // Levante
    718, // Real Oviedo
];

// Jugadores soportados (IDs de jugadores que tienen imagen local)
// Por ahora solo el default, pero se puede expandir
export const SUPPORTED_PLAYERS = [
    // Se puede agregar más IDs de jugadores aquí cuando se tengan las imágenes
];

// Logos soportados
export const SUPPORTED_LOGOS = {
    // Fantasy Providers
    BIWENGER: 'biwenger-logo.png',
    FANTASY_MARCA: 'fantasy-marca.png',
    MISTER: 'mister.png',
    PREDICTING_MANAGER: 'predictingmanagers.png',
    RUleta_FANTASY: 'ruleta-fantasy.png',
    LA_LIGA: 'la-liga.png',

    // Analitica Fantasy Logos
    SOLO_LETRAS_BLANCO: 'blanco-letras.png',
    FONDO_OSCURO_LETRAS_VERDES: 'fondo-oscuro_letras-verdes.png',
    FONDO_VERDE_LETRAS_BLANCAS: 'fondo-verde_letras-blancas.png',
    FONDO_VERDE_LETRAS_OSCURAS_BORDER: 'fondo-verde_letras-oscuras_border.png',
    FONDO_VERDE_LETRAS_OSCURAS_CIRCLE: 'fondo-verde_letras-oscuras_circle.png',
    FONDO_VERDE_LETRAS_OSCURAS: 'fondo-verde_letras-oscuras.png',
    SOLO_LETRAS_NEGRAS: 'letras-texto-negro.png',
    TEXTO_Y_LETRAS_VERDES: 'letras-texto-verde.png',
    LETRAS_VERDES_TEXTO_OSCURO: 'letras-verde_texto-negro.png',
    SOLO_LETRAS_VERDES: 'verde-letras.png',
    NEGRO_LETRAS: 'negro-letras.png',
    LOGO: 'logo.png',
};

// Lotties soportados
export const SUPPORTED_LOTTIES = {
    OK_STATUS: 'ok-status.lottie',
    INJURED_RED: 'injured-red.lottie',
    INJURED_BLACK: 'injured-black.lottie',
    EXTRADEPORTIVO: 'extradeportivo.lottie',
    DOUBT_GREEN: 'doubt-green.lottie',
    DOUBT_ORANGE: 'doubt-orange.lottie',
    RIGHT_ARROW: 'right-arrow.lottie',
    RED_CARD: 'red-card.lottie',
    FIRE: 'fire.lottie',
    MONEY: 'money.lottie',
    MONEY2: 'money2.lottie',
    SOCCER_FIELD: 'soccer-field.lottie',
    SOCCER_GOAL: 'soccer-goal.lottie',
    TIPS: 'tips.lottie',
    MARVIN: 'marvin.lottie',
    JARVIS: 'jarvis.lottie',
    CLOWN: 'clown.lottie',
    LOADING: 'loading.lottie',
    CLOUDS: 'weather/clouds.lottie',
    RAIN: 'weather/rain.lottie',
    SNOW: 'weather/snow.lottie',
    SNOWING: 'weather/snowing.lottie',
    SUN_CLOUDS: 'weather/sunny-cloud.lottie',
    SUNNY: 'weather/sunny.lottie',
    WEATHER: 'weather/weather.lottie',
    MIST: 'weather/mist.lottie',
    CHAT: 'chat.lottie',
    ATLETICO: 'equipos/atletico.lottie',
    BARCELONA: 'equipos/barcelona.lottie',
    REAL_MADRID: 'equipos/real-madrid.lottie',
    INFO: 'info.lottie',
    SHARE: 'share.lottie',
    SPINNER1: 'spinners/spinner1.lottie',
    SPINNER2: 'spinners/spinner2.lottie',
    SPINNER3: 'spinners/spinner3.lottie',
    SPINNER4: 'spinners/spinner4.lottie',
    SPINNER5: 'spinners/spinner5.lottie',
    SPINNER6: 'spinners/spinner6.lottie',
    SPINNER7: 'spinners/spinner7.lottie',
    SPINNER8: 'spinners/spinner8.lottie',
    SPINNER9: 'spinners/spinner9.lottie',
    OFFSIDE: 'offside.lottie',
    EURO_UP: 'euro-up.lottie',
    EURO_DOWN: 'euro-down.lottie',
    TOP: 'top.lottie',
};

/**
 * Verifica si un equipo tiene imagen local disponible
 * @param {number|string} teamId - ID del equipo
 * @returns {boolean}
 */
export const isTeamSupported = (teamId) => {
    if (!teamId) return false;
    const id = typeof teamId === 'number' ? teamId : Number(teamId);
    return !isNaN(id) && SUPPORTED_TEAMS.includes(id);
};

/**
 * Verifica si un jugador tiene imagen local disponible
 * @param {number|string} playerId - ID del jugador
 * @returns {boolean}
 */
export const isPlayerSupported = (playerId) => {
    if (!playerId) return false;
    const id = typeof playerId === 'number' ? playerId : Number(playerId);
    return !isNaN(id) && SUPPORTED_PLAYERS.includes(id);
};

/**
 * Verifica si un logo tiene imagen local disponible
 * @param {string} logoKey - Clave del logo
 * @returns {boolean}
 */
export const isLogoSupported = (logoKey) => {
    if (!logoKey) return false;
    return Object.values(SUPPORTED_LOGOS).includes(logoKey) ||
        Object.keys(SUPPORTED_LOGOS).includes(logoKey);
};

/**
 * Verifica si un lottie tiene archivo local disponible
 * @param {string} lottieKey - Clave del lottie
 * @returns {boolean}
 */
export const isLottieSupported = (lottieKey) => {
    if (!lottieKey) return false;
    return Object.values(SUPPORTED_LOTTIES).includes(lottieKey) ||
        Object.keys(SUPPORTED_LOTTIES).includes(lottieKey);
};

